/**
 * UAVTradeZone Modification Record
 *
 * Change ID:    BROADCAST_HELPERS_EXTRACTION
 * Date:         2025-12-29
 * Modified By:  Cursor
 *
 * Description:
 * Extracted broadcast formatting and styling helpers from DashboardBroadcastSummaryTable
 * to a dedicated utility file to reduce component size and improve maintainability.
 *
 * Related Screens / Modules:
 * - client/src/features/dashboard/DashboardBroadcastSummaryTable.tsx
 */

// [2025-12-29] Format relative time (e.g., "2 days ago", "1 hour ago")
export const formatRelativeTime = (dateString?: string): string => {
  if (!dateString) return "—";
  const date = new Date(dateString);
  const now = new Date();
  const diff = now.getTime() - date.getTime();
  const minutes = Math.floor(diff / 60000);
  const hours = Math.floor(diff / 3600000);
  const days = Math.floor(diff / 86400000);

  if (days > 0) {
    return `${days} day${days > 1 ? "s" : ""} ago`;
  } else if (hours > 0) {
    return `${hours} hour${hours > 1 ? "s" : ""} ago`;
  } else {
    return `${Math.max(1, minutes)} min ago`;
  }
};

// [2026-02-10] compact-time: Short format for mobile cards ("16h ago", "2d ago", "5m ago")
export const formatRelativeTimeCompact = (dateString?: string): string => {
  if (!dateString) return "—";
  const date = new Date(dateString);
  const now = new Date();
  const diff = now.getTime() - date.getTime();
  const minutes = Math.floor(diff / 60000);
  const hours = Math.floor(diff / 3600000);
  const days = Math.floor(diff / 86400000);

  if (days > 0) return `${days}d ago`;
  if (hours > 0) return `${hours}h ago`;
  return `${Math.max(1, minutes)}m ago`;
};

// [2026-02-13] CURRENCY_PHASE2: getCurrencySymbol and formatPrice moved to utils/priceDisplay.ts

// [2025-12-29] Get background color for condition label
export const getConditionBackgroundColor = (condition?: string | null): string => {
  if (!condition) return "transparent";
  const normalized = condition.toLowerCase().trim();
  
  if (normalized.includes("new retail") || normalized.includes("new bulk")) {
    return "var(--color-status-green-light)";
  }
  
  if (normalized.includes("used")) {
    return "var(--color-status-orange-light)";
  }
  
  if (normalized.includes("refurbished")) {
    return "var(--color-status-blue-light)";
  }
  
  if (normalized.includes("as-is") || normalized.includes("as is")) {
    return "var(--color-status-purple-light)";
  }
  
  return "transparent";
};

// [2025-12-29] Get text color for condition label
export const getConditionTextColor = (condition?: string | null): string => {
  if (!condition) return "inherit";
  const normalized = condition.toLowerCase().trim();
  
  if (normalized.includes("new retail") || normalized.includes("new bulk")) {
    return "var(--color-status-green)";
  }
  
  if (normalized.includes("used")) {
    return "var(--color-status-orange)";
  }
  
  if (normalized.includes("refurbished")) {
    return "var(--color-status-blue)";
  }
  
  if (normalized.includes("as-is") || normalized.includes("as is")) {
    return "var(--color-status-purple)";
  }
  
  return "inherit";
};
